import { useState, useMemo } from "react";
import { IconButton, Box, Tooltip } from "@mui/material";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import NoteAddRoundedIcon from "@mui/icons-material/NoteAddRounded";
import DeleteIcon from "@mui/icons-material/Delete";
import ListAltRoundedIcon from "@mui/icons-material/ListAltRounded";

import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import { deleteOperation } from "../../../services/OperationService";
import { createNotificationCertificateFromOperation } from "../../../services/NotificationCertificateService";
import DatagridCustom from "../../General/DatagridCustom";
import AppDialog from "../../General/Dialog/AppDialog";
import ConfirmDialog from "../../General/Dialog/ConfirmDialog";
import NotificationCertificateFromOperationModal from "../../NotificationCertificate/NotificationCertificateFromOperationModal";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import { isLegalFileLocked, LOCKED_LEGAL_FILE_MESSAGE } from "./detailHelpers";

export default function TabOperaciones({ operations, legalFile, onRefresh }) {
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const locked = isLegalFileLocked(legalFile);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedHtml, setSelectedHtml] = useState("");

  const [openDeleteOperationConfirm, setOpenDeleteOperationConfirm] = useState(false);
  const [selectedOperationId, setSelectedOperationId] = useState(null);

  const [openOperationCertModal, setOpenOperationCertModal] = useState(false);
  const [selectedOperationForCert, setSelectedOperationForCert] = useState(null);
  const [operationCertLoading, setOperationCertLoading] = useState(false);
  const [operationCertError, setOperationCertError] = useState("");

  const operationOptions = useMemo(() => {
    return (legalFile?.operations || [])
      .slice()
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [legalFile]);

  const handleViewHtml = (htmlContent) => {
    setSelectedHtml(htmlContent);
    setModalOpen(true);
  };

  const handleDeleteOperation = (opId) => {
    setSelectedOperationId(opId);
    setOpenDeleteOperationConfirm(true);
  };

  const handleConfirmDeleteOperation = async () => {
    if (!selectedOperationId) return;

    try {
      const res = await deleteOperation(selectedOperationId);
      if (res.data?.status === "success") {
        showToast(res.data.message, "success");
        setOpenDeleteOperationConfirm(false);
        setSelectedOperationId(null);
        await onRefresh();
      }
    } catch {
      showToast("Error al eliminar operación.", "error");
    }
  };

  const handleOpenOperationCertModal = (operation) => {
    setSelectedOperationForCert(operation);
    setOpenOperationCertModal(true);
  };

  const handleCloseOperationCertModal = () => {
    if (operationCertLoading) return;
    setOpenOperationCertModal(false);
    setSelectedOperationForCert(null);
    setOperationCertError("");
  };

  const handleConfirmFromOperation = async (data) => {
    if (!data?.operation) {
      setOperationCertError("Debe seleccionar una operación principal.");
      return;
    }

    if (!data?.party) {
      setOperationCertError("No se encontró el demandado principal.");
      return;
    }

    if (!data?.selectedAddress?.address) {
      setOperationCertError("Debe seleccionar o cargar un domicilio.");
      return;
    }

    try {
      setOperationCertLoading(true);
      setOperationCertError("");

      const formData = new FormData();
      formData.append("operation_code", data.operation.number_operation);
      if (data.secondaryOperation?.number_operation) {
        formData.append(
          "secondary_operation_code",
          data.secondaryOperation.number_operation,
        );
      }
      formData.append("template_html", data.templateHtml || "");
      formData.append("party_id", data.party.id);
      formData.append(
        "selected_address[type]",
        data.selectedAddress.type || "",
      );
      formData.append(
        "selected_address[address]",
        data.selectedAddress.address || "",
      );
      formData.append(
        "selected_address[city]",
        data.selectedAddress.city || "",
      );
      formData.append(
        "selected_address[from_date]",
        data.selectedAddress.from_date || "",
      );
      formData.append(
        "selected_address[is_manual]",
        data.selectedAddress.is_manual ? "1" : "0",
      );
      if (data.attachmentFile) {
        formData.append("attachment", data.attachmentFile);
      }

      const res = await createNotificationCertificateFromOperation(
        legalFile.id,
        formData,
      );

      if (res.data?.status === "success") {
        showToast(res.data.message || "Cédula generada correctamente.", "success");
        setOpenOperationCertModal(false);
        setSelectedOperationForCert(null);
        await onRefresh();
      }
    } catch (error) {
      setOperationCertError(
        error?.response?.data?.message ||
          "No se pudo generar la cédula de notificación.",
      );
    } finally {
      setOperationCertLoading(false);
    }
  };

  const columnsOperations = [
    { field: "number_operation", headerName: "N° Operación" },
    {
      field: "date",
      headerName: "Fecha",
      flex: 1,
      renderCell: (params) => {
        const date = new Date(params.value);
        if (isNaN(date.getTime())) return "Fecha inválida";
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        return `${day}/${month}/${year} ${hours}:${minutes}`;
      },
    },
    { field: "type", headerName: "Tipo", flex: 2 },
    { field: "location", headerName: "Ubicación", flex: 1 },
    { field: "presented", headerName: "Presentado por", flex: 1 },
    { field: "status", headerName: "Estado", flex: 1 },
    {
      field: "actions",
      headerName: "Acciones",
      sortable: false,
      flex: 1,
      renderCell: (params) => (
        <>
          <IconButton
            color="primary"
            onClick={() => handleViewHtml(params.row.text)}
            disabled={!hasPermission("ver detalle operacion")}
          >
            <VisibilityRoundedIcon />
          </IconButton>

          <Tooltip title={locked ? LOCKED_LEGAL_FILE_MESSAGE : ""}>
            <span>
              <IconButton
                color="primary"
                onClick={() => handleOpenOperationCertModal(params.row)}
                disabled={!hasPermission("generar cedula notificacion") || locked}
              >
                <NoteAddRoundedIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title={locked ? LOCKED_LEGAL_FILE_MESSAGE : ""}>
            <span>
              <IconButton
                color="error"
                onClick={() => handleDeleteOperation(params.row.id)}
                disabled={!hasPermission("eliminar operacion") || locked}
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <>
      <DatagridCustom
        rows={operations}
        columns={columnsOperations}
        icon={<ListAltRoundedIcon />}
        title="Operaciones del Expediente"
        description="Historial de operaciones registradas en el expediente."
        getRowId={(row) => row.id}
      />

      <AppDialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Detalle de la Operación"
        maxWidth="lg"
        fullScreenMobile
        fullScreenBreakpoint="md"
        stickyHeader
        contentSx={{ p: 2 }}
        paperSx={{ borderRadius: isMobile ? 0 : 4 }}
      >
        <Box sx={{ maxWidth: "100%", overflowX: "auto" }}>
          <div dangerouslySetInnerHTML={{ __html: selectedHtml }} />
        </Box>
      </AppDialog>

      <ConfirmDialog
        open={openDeleteOperationConfirm}
        onClose={() => {
          setOpenDeleteOperationConfirm(false);
          setSelectedOperationId(null);
        }}
        onConfirm={handleConfirmDeleteOperation}
        title="Confirmar eliminación"
        description="¿Querés eliminar esta operación? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        cancelText="Cancelar"
        confirmColor="error"
      />

      <NotificationCertificateFromOperationModal
        open={openOperationCertModal}
        onClose={handleCloseOperationCertModal}
        onConfirm={handleConfirmFromOperation}
        loading={operationCertLoading}
        legalFile={legalFile}
        selectedOperation={selectedOperationForCert}
        operationOptions={operationOptions}
        apiError={operationCertError}
        onClearApiError={() => setOperationCertError("")}
      />
    </>
  );
}
